<div
  class="crm-scroll flex h-full min-h-0 min-w-0 flex-col overflow-auto text-slate-900"
  [ngClass]="configuration ? 'gap-4 bg-slate-50 p-4' : 'gap-2 px-3 pb-3 pt-3'"
>
  @if (configuration) {
    <header class="flex shrink-0 items-center gap-3">
      <div class="grid h-11 w-11 place-items-center rounded-xl bg-orange-100 text-orange-600"><app-icon>receipt_long</app-icon></div>
      <div class="min-w-0 flex-1">
        <h1 class="!m-0 !text-xl !font-bold">{{ 'prro.settings_title' | translate }}</h1>
        <p class="!m-0 text-sm text-slate-500">{{ 'prro.settings_hint' | translate }}</p>
      </div>
      <app-button btnType="icon" [ariaLabel]="'close' | translate" (onChangeClick)="close.emit()"><app-icon>close</app-icon></app-button>
    </header>
  } @else {
    <header class="flex shrink-0 flex-col gap-2 lg:flex-row lg:items-center lg:gap-4">
      <h1 class="!m-0 !text-[30px] !font-bold !leading-none !text-white md:!text-[40px] md:!leading-tight">{{ 'prro.title' | translate }}</h1>
      <nav class="crm-scroll flex min-w-0 flex-1 items-center gap-1 overflow-x-auto !overflow-y-hidden py-1.5">
        @for (value of tabs; track value) {
          <app-button
            class="inline-flex shrink-0 leading-none"
            btnType="stroked"
            [customClass]="'!inline-flex !h-9 !items-center !gap-1.5 !rounded-lg !border-0 !px-3 !text-sm !font-semibold !tracking-normal ' + (tab === value ? '!bg-white !text-slate-900 shadow-sm' : '!bg-transparent !text-white/80 hover:!bg-white/10 hover:!text-white')"
            (onChangeClick)="selectTab(value)"
          >
            <span class="inline-flex items-center gap-1.5">
              <app-icon class="!flex !h-[18px] !w-[18px] !text-[18px] !leading-none">{{ value === 'registers' ? 'point_of_sale' : value === 'operations' ? 'receipt_long' : 'history' }}</app-icon>
              {{ 'prro.' + value | translate }}
            </span>
          </app-button>
        }
      </nav>
      <div class="flex shrink-0 items-center gap-1 self-end lg:self-auto">
        <app-button
          btnType="icon"
          customClass="!inline-flex !h-9 !w-9 !items-center !justify-center !rounded-lg !p-0 !text-white/80 hover:!bg-white/10 hover:!text-white"
          [disabled]="loading || busy"
          [ariaLabel]="'prro.refresh' | translate"
          [matTooltip]="'prro.refresh' | translate"
          (onChangeClick)="load()"
        ><app-icon class="!flex !h-5 !w-5 !text-[20px] !leading-none">refresh</app-icon></app-button>
        <app-button
          btnType="icon"
          customClass="!inline-flex !h-9 !w-9 !items-center !justify-center !rounded-lg !p-0 !text-white/80 hover:!bg-white/10 hover:!text-white"
          [disabled]="loading || busy"
          [ariaLabel]="'prro.settings_title' | translate"
          [matTooltip]="'prro.settings_title' | translate"
          (onChangeClick)="openSettings()"
        ><app-icon class="!flex !h-5 !w-5 !text-[20px] !leading-none">settings</app-icon></app-button>
      </div>
    </header>
  }
  @if (loading) {
    <div class="h-32 animate-pulse rounded-xl bg-slate-200"></div>
  } @else if (error) {
    <section class="rounded-xl border !border-amber-200 bg-amber-50 p-5">
      <p>{{ error | translate }}</p>
      <app-button (onChangeClick)="load()">{{ 'prro.retry' | translate }}</app-button>
    </section>
  } @else if (configuration) {
    @if (!settings.can_manage) {
      <p class="rounded-lg bg-amber-50 p-3 text-amber-800">{{ 'prro.owner_only' | translate }}</p>
    }
    <section class="flex flex-col gap-3 rounded-xl border !border-slate-200 bg-white p-4">
      <div class="flex items-center gap-3">
        <h2 class="!m-0 !text-base !font-semibold flex-1">{{ 'prro.registers' | translate }}</h2>
        @if (settings.can_manage && !showArchived) {
          <app-button [disabled]="busy" (onChangeClick)="openForm(null)">{{ 'prro.connect' | translate }}</app-button>
        }
      </div>
      <p class="!m-0 text-sm text-slate-500">{{ 'prro.role_access' | translate }}</p>
      @if (settings.can_manage) {
        <div class="flex flex-wrap gap-2">
          <app-button
            btnType="stroked"
            [disabled]="busy"
            [customClass]="!showArchived ? '!border-orange-300 !bg-orange-50 !text-orange-700' : ''"
            (onChangeClick)="selectRegisterView(false)"
          >{{ 'prro.registers' | translate }}</app-button>
          <app-button
            btnType="stroked"
            [disabled]="busy"
            [customClass]="showArchived ? '!border-orange-300 !bg-orange-50 !text-orange-700' : ''"
            (onChangeClick)="selectRegisterView(true)"
          >{{ 'prro.archive' | translate }}</app-button>
        </div>
      }
      @if (showArchived) {
        <p class="!m-0 text-sm text-slate-500">{{ 'prro.archive_hint' | translate }}</p>
      }
      @if (showArchived && archiveLoading) {
        <div class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
      } @else if (showArchived && archiveError) {
        <div class="rounded-lg bg-amber-50 p-3 text-amber-800">
          <p>{{ archiveError | translate }}</p>
          <app-button (onChangeClick)="loadArchive()">{{ 'prro.retry' | translate }}</app-button>
        </div>
      } @else {
        @for (register of (showArchived ? archivedRegisters : registers); track register.id) {
          <div class="flex flex-wrap items-center gap-3 rounded-lg border !border-slate-100 p-3">
            <div class="flex-1">
              <strong>{{ register.name }}</strong>
              <div class="text-xs text-slate-500">
                {{ register.fiscal_number }} · {{ (showArchived ? 'prro.archived' : register.is_active ? 'prro.active' : 'prro.disabled') | translate }}
                @if (register.is_test) {
                  · {{ 'prro.test' | translate }}
                }
              </div>
            </div>
            @if (settings.can_manage) {
              <div class="flex shrink-0 items-center gap-2">
                @if (showArchived) {
                  <app-button
                    customClass="!h-9 !rounded-lg !px-3 !text-sm !font-medium !tracking-normal"
                    [disabled]="busy"
                    (onChangeClick)="restore(register)"
                  >
                    <span class="inline-flex items-center justify-center gap-2 whitespace-nowrap align-middle leading-none">
                      <app-icon class="!flex !h-5 !w-5 shrink-0 !text-[20px] !leading-none">unarchive</app-icon>
                      <span>{{ 'prro.restore' | translate }}</span>
                    </span>
                  </app-button>
                } @else {
                  <app-toggle
                    [ngModel]="register.is_active"
                    (ngModelChange)="toggle(register, $event)"
                    [disabled]="busy"
                    [matTooltip]="(register.is_active ? 'prro.disable' : 'prro.enable') | translate"
                  ><span class="sr-only">{{ 'prro.active' | translate }}</span></app-toggle>
                  <app-button
                    btnType="icon"
                    [disabled]="busy"
                    [ariaLabel]="'edit' | translate"
                    [matTooltip]="'edit' | translate"
                    (onChangeClick)="openForm(register)"
                  ><app-icon>edit</app-icon></app-button>
                  <app-button
                    btnType="icon"
                    [disabled]="busy"
                    [ariaLabel]="'prro.archive_action' | translate"
                    [matTooltip]="'prro.archive_action' | translate"
                    (onChangeClick)="archive(register)"
                  ><app-icon>archive</app-icon></app-button>
                }
              </div>
            }
          </div>
        } @empty {
          <p class="text-sm text-slate-500">{{ (showArchived ? 'prro.empty_archive' : 'prro.empty_registers') | translate }}</p>
        }
      }
    </section>
    <section class="flex flex-col gap-4 rounded-xl border !border-slate-200 bg-white p-4">
      <h2 class="!m-0 !text-base !font-semibold">{{ 'prro.tax_settings' | translate }}</h2>
      <app-select
        class="max-w-xl"
        [(ngModel)]="settings.default_tax_group"
        [options]="taxOptions"
        optionLabel="name"
        optionValue="id"
        [isTranslate]="true"
        [showClear]="true"
        [disabled]="!settings.can_manage || busy"
        [label]="'prro.tax_group' | translate"
      />
      <p class="!m-0 text-sm text-slate-500">{{ 'prro.tax_hint' | translate }}</p>
      <h2 class="!m-0 !text-base !font-semibold">{{ 'prro.mappings' | translate }}</h2>
      @for (mapping of settings.payment_mappings; track $index) {
        <div class="grid items-end gap-3 rounded-lg bg-slate-50 p-3 md:grid-cols-2">
          <app-select
            [(ngModel)]="mapping.payment_type_id"
            [options]="typeOptions"
            optionLabel="name"
            optionValue="id"
            [disabled]="!settings.can_manage || busy"
            [label]="'board_payments.fields.payment_type' | translate"
          />
          <app-select
            [(ngModel)]="mapping.payment_service_id"
            [options]="serviceOptions"
            optionLabel="name"
            optionValue="id"
            [showClear]="true"
            [disabled]="!settings.can_manage || busy"
            [label]="'board_payments.fields.payment_service' | translate"
          />
          <app-select
            [ngModel]="mapping.payment_code"
            (ngModelChange)="setPaymentCode(mapping, $event)"
            [options]="paymentOptions"
            optionDisabled="disabled"
            optionLabel="name"
            optionValue="id"
            [isTranslate]="true"
            [required]="true"
            [disabled]="!settings.can_manage || busy"
            [label]="'prro.payment_method' | translate"
          />
          <div class="flex items-center gap-2">
            <app-toggle
              [(ngModel)]="mapping.requires_card_metadata"
              [disabled]="!settings.can_manage || busy || mapping.payment_code === 2"
              >{{ 'prro.card_metadata' | translate }}</app-toggle
            >
            @if (settings.can_manage) {
              <app-button
                btnType="icon"
                [disabled]="busy"
                [ariaLabel]="'delete' | translate"
                (onChangeClick)="settings.payment_mappings.splice($index, 1)"
                ><app-icon>delete_outline</app-icon></app-button
              >
            }
          </div>
        </div>
      } @empty {
        <p class="text-sm text-slate-500">{{ 'prro.empty_mappings' | translate }}</p>
      }
      <p class="!m-0 text-xs text-slate-500">{{ 'prro.mapping_hint' | translate }}</p>
      @if (settings.can_manage) {
        <div class="flex gap-2">
          <app-button btnType="stroked" [disabled]="busy" (onChangeClick)="addMapping()">{{
            'prro.add_mapping' | translate
          }}</app-button
          ><app-button [disabled]="busy" (onChangeClick)="saveSettings()">{{ 'save' | translate }}</app-button>
        </div>
      }
    </section>
  } @else {
    @if (tab === 'registers') {
      <div class="flex flex-col divide-y divide-slate-200 overflow-hidden rounded-xl border !border-slate-200 bg-white">
        @for (register of registers; track register.id) {
          <section class="grid items-center gap-4 p-4 xl:grid-cols-[minmax(12rem,1fr)_minmax(28rem,2fr)_auto]">
            <div class="flex min-w-0 items-start gap-2">
              <app-icon class="text-orange-500">point_of_sale</app-icon>
              <div class="min-w-0 flex-1">
                <strong class="block break-words">{{ register.name }}</strong>
                <p class="!m-0 text-xs text-slate-500">{{ register.fiscal_number }}</p>
              </div>
              <span class="shrink-0 self-center rounded bg-slate-100 px-2 py-1 text-xs">{{
                (register.is_active ? 'prro.active' : 'prro.disabled') | translate
              }}</span>
            </div>
            @let status = statusesById[register.id];
            <div class="min-w-0">
              @if (status) {
                <dl class="!m-0 grid grid-cols-2 gap-4 text-sm sm:grid-cols-[1fr_1fr_1.5fr_1fr]">
                  <div class="min-w-0">
                    <dt class="mb-1 text-xs font-medium text-slate-500">{{ 'prro.shift' | translate }}</dt>
                    <dd class="!m-0 break-words font-medium tabular-nums">
                      {{ (status.is_open === null ? 'prro.unknown' : status.is_open ? 'prro.open' : 'prro.closed') | translate }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="mb-1 text-xs font-medium text-slate-500">{{ 'prro.connection' | translate }}</dt>
                    <dd class="!m-0 break-words font-medium tabular-nums">
                      {{ (status.is_online === null ? 'prro.unknown' : status.is_online ? 'prro.online' : 'prro.offline') | translate }}
                    </dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="mb-1 text-xs font-medium text-slate-500">{{ 'prro.opened_at' | translate }}</dt>
                    <dd class="!m-0 break-words font-medium tabular-nums">{{ status.is_open === true ? formatOpenedAt(status.opened_at) : '—' }}</dd>
                  </div>
                  <div class="min-w-0">
                    <dt class="mb-1 text-xs font-medium text-slate-500">{{ 'prro.balance' | translate }}</dt>
                    <dd class="!m-0 break-words font-medium tabular-nums">{{ status.cash_balance ?? '—' }}</dd>
                  </div>
                </dl>
                @if (status.result.error) {
                  <p class="!m-0 mt-2 text-xs text-red-700">{{ 'prro.status_failed' | translate }}</p>
                }
              } @else {
                <p class="!m-0 text-sm text-slate-500">{{ 'prro.status_unknown' | translate }}</p>
              }
            </div>
            <div class="flex shrink-0 items-center justify-end gap-2">
              <app-button
                [btnType]="status?.is_open === true ? 'stroked' : 'default'"
                customClass="!inline-flex !h-9 !items-center !rounded-lg !px-3 !text-sm !font-semibold !tracking-normal disabled:!border-transparent disabled:!bg-slate-100 disabled:!text-slate-400"
                [disabled]="busy || !register.is_active || (status?.is_open !== true && status?.is_open !== false)"
                (onChangeClick)="command(register, status?.is_open === true ? 'close' : 'open')"
              >{{ (status?.is_open === true ? 'prro.close_shift' : 'prro.open_shift') | translate }}</app-button>
              <app-button
                btnType="icon"
                customClass="!inline-flex !h-9 !w-9 !items-center !justify-center !rounded-lg !p-0 !text-slate-500 hover:!bg-slate-100 hover:!text-slate-900"
                [ariaLabel]="'prro.details' | translate"
                [matMenuTriggerFor]="registerMenu"
                #registerMenuTrigger="matMenuTrigger"
              ><app-icon class="!flex !h-5 !w-5 !text-[20px] !leading-none">more_vert</app-icon></app-button>
              <mat-menu #registerMenu="matMenu" xPosition="before">
                <button
                  mat-menu-item
                  type="button"
                  class="!min-h-10 !gap-3 !px-3"
                  [disabled]="!register.is_active || statusBusy.has(register.id)"
                  (click)="registerMenuTrigger.closeMenu(); refreshStatus(register.id)"
                >
                  <app-icon matMenuItemIcon class="!h-5 !w-5 !text-[20px] !leading-none">refresh</app-icon>
                  <span class="text-sm font-medium tracking-normal">{{ 'prro.refresh_status' | translate }}</span>
                </button>
                <button
                  mat-menu-item
                  type="button"
                  class="!min-h-10 !gap-3 !px-3"
                  [disabled]="busy || !register.is_active || status?.is_open !== true"
                  (click)="registerMenuTrigger.closeMenu(); command(register, 'x-report')"
                >
                  <app-icon matMenuItemIcon class="!h-5 !w-5 !text-[20px] !leading-none">description</app-icon>
                  <span class="text-sm font-medium tracking-normal">{{ 'prro.x_report' | translate }}</span>
                </button>
                <button
                  mat-menu-item
                  type="button"
                  class="!min-h-10 !gap-3 !px-3"
                  (click)="registerMenuTrigger.closeMenu(); showHistory(register.id)"
                >
                  <app-icon matMenuItemIcon class="!h-5 !w-5 !text-[20px] !leading-none">history</app-icon>
                  <span class="text-sm font-medium tracking-normal">{{ 'prro.provider_history' | translate }}</span>
                </button>
              </mat-menu>
            </div>
          </section>
        } @empty {
          <p class="rounded-lg bg-white p-5 text-slate-500">{{ 'prro.empty_registers' | translate }}</p>
        }
      </div>
    } @else {
      <section class="flex flex-col gap-3 rounded-xl border !border-slate-200 bg-white p-4">
        <div class="flex flex-wrap gap-3">
          <app-select
            class="w-60"
            [(ngModel)]="registerId"
            [options]="registers"
            optionLabel="name"
            optionValue="id"
            [showClear]="true"
            [placeholder]="'prro.all_registers' | translate"
            (ngModelChange)="filter()"
          />
          @if (tab === 'operations') {
            <app-select
              class="w-52"
              [(ngModel)]="statusFilter"
              [options]="statuses"
              optionLabel="name"
              optionValue="id"
              [isTranslate]="true"
              [showClear]="true"
              [placeholder]="'prro.all_statuses' | translate"
              (ngModelChange)="filter()"
            /><app-select
              class="w-52"
              [(ngModel)]="task"
              [options]="tasks"
              optionLabel="name"
              optionValue="id"
              [isTranslate]="true"
              [showClear]="true"
              [placeholder]="'prro.all_tasks' | translate"
              (ngModelChange)="filter()"
            />
          } @else {
            <app-select
              class="w-52"
              [(ngModel)]="shiftStatus"
              [options]="shiftStatuses"
              optionLabel="name"
              optionValue="id"
              [isTranslate]="true"
              [showClear]="true"
              [placeholder]="'prro.all_statuses' | translate"
              (ngModelChange)="filter()"
            />
          }
        </div>
        @if (journalError) {
          <p class="text-red-700">{{ journalError | translate }}</p>
        }
        @if (tab === 'operations') {
          <app-table-v2
            [columns]="operationColumns"
            [rows]="operations"
            [trackBy]="trackById"
            [loading]="journalLoading"
            emptyText="prro.empty_journal"
            minWidth="min-w-[700px]"
          >
            <ng-template appTableV2Cell="register" [appTableV2CellOf]="operations" let-operation>{{
              registerName(operation.cash_register_id)
            }}</ng-template>
            <ng-template appTableV2Cell="date" [appTableV2CellOf]="operations" let-operation>{{
              operation.created_at | date: 'dd.MM.yyyy HH:mm'
            }}</ng-template>
            <ng-template appTableV2Cell="status" [appTableV2CellOf]="operations" let-operation
              >{{ 'prro.status.' + operation.status | translate }}
              <div class="text-xs text-slate-500">{{ 'prro.task.' + operation.task | translate }}</div></ng-template
            >
            <ng-template appTableV2Cell="receipt" [appTableV2CellOf]="operations" let-operation>
              <span class="break-all font-mono text-xs text-slate-700">{{ operation.fiscal_receipt_number || '—' }}</span>
            </ng-template>
            <ng-template appTableV2Cell="details" [appTableV2CellOf]="operations" let-operation>
              <div class="ml-auto flex w-fit items-center gap-1">
                <div class="h-9 w-9 shrink-0">
                  @if (operation.task !== 10 && operation.task !== 11 && operation.receipt_url) {
                    <a
                      class="inline-flex h-9 w-9 items-center justify-center rounded-lg !p-0 !leading-none !text-orange-600 !no-underline hover:bg-orange-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                      [href]="operation.receipt_url"
                      [attr.aria-label]="'prro.receipt_link' | translate"
                      [matTooltip]="'prro.receipt_link' | translate"
                      target="_blank"
                      rel="noopener noreferrer"
                    ><app-icon class="!flex !h-5 !w-5 !items-center !justify-center !text-[20px] !leading-none">open_in_new</app-icon></a>
                  }
                </div>
                <app-button
                  btnType="icon"
                  customClass="!inline-flex !h-9 !w-9 !items-center !justify-center !rounded-lg !p-0 !leading-none !text-slate-500 hover:!bg-slate-100 hover:!text-slate-900"
                  [disabled]="busy"
                  [ariaLabel]="'prro.details' | translate"
                  [matTooltip]="'prro.details' | translate"
                  (onChangeClick)="inspect(operation)"
                ><app-icon class="!flex !h-5 !w-5 !items-center !justify-center !text-[20px] !leading-none">info</app-icon></app-button>
              </div>
            </ng-template>
          </app-table-v2>
        } @else {
          <app-table-v2
            [columns]="shiftColumns"
            [rows]="shifts"
            [trackBy]="trackById"
            [loading]="journalLoading"
            emptyText="prro.empty_journal"
            minWidth="min-w-[700px]"
          >
            <ng-template appTableV2Cell="register" [appTableV2CellOf]="shifts" let-shift>{{
              registerName(shift.cash_register_id)
            }}</ng-template>
            <ng-template appTableV2Cell="date" [appTableV2CellOf]="shifts" let-shift>
              <div class="flex flex-col gap-1 text-sm tabular-nums">
                <div><span class="mr-2 text-xs text-slate-500">{{ 'prro.opened_at' | translate }}:</span>{{ formatOpenedAt(shift.opened_at) }}</div>
                <div><span class="mr-2 text-xs text-slate-500">{{ 'prro.history_closed_at' | translate }}:</span>{{ formatOpenedAt(shift.closed_at) }}</div>
              </div>
            </ng-template>
            <ng-template appTableV2Cell="status" [appTableV2CellOf]="shifts" let-shift>{{
              'prro.shift_status.' + shift.status | translate
            }}</ng-template>
            <ng-template appTableV2Cell="duration" [appTableV2CellOf]="shifts" let-shift>
              @if (shiftDuration(shift.opened_at, shift.closed_at); as duration) {
                <span class="tabular-nums">
                  @if (duration.days) { {{ 'prro.duration_days' | translate: { count: duration.days } }} }
                  {{ duration.clock }}
                </span>
              } @else { <span class="text-slate-400">—</span> }
            </ng-template>
            <ng-template appTableV2Cell="details" [appTableV2CellOf]="shifts" let-shift
              ><app-button
                btnType="stroked"
                (onChangeClick)="showHistory(shift.cash_register_id, shift.external_id)"
                >{{ 'prro.receipts' | translate }}</app-button
              ></ng-template
            >
          </app-table-v2>
        }
        <div class="flex items-center justify-end gap-3 text-sm text-slate-500">
          <span>{{ page }} · {{ total }}</span
          ><app-button btnType="stroked" [disabled]="journalLoading || page <= 1" (onChangeClick)="changePage(-1)">{{
            'prro.previous' | translate
          }}</app-button
          ><app-button
            btnType="stroked"
            [disabled]="journalLoading || page * 25 >= total"
            (onChangeClick)="changePage(1)"
            >{{ 'prro.next' | translate }}</app-button
          >
        </div>
      </section>
    }
  }
</div>
